import { makeAutoObservable } from "mobx"

import emailKnowledgeService, {
  EmailKnowledgeApprovePayload,
  EmailKnowledgeStatus,
  EmailKnowledgeSubmission,
  EmailKnowledgeUpdatePayload,
} from "@services/email-knowledge.service"

export class EmailKnowledgeStore {
  constructor() {
    makeAutoObservable(this)
  }

  items: EmailKnowledgeSubmission[] = []

  total = 0

  isLoading = false

  isActionLoading = false

  error: string | null = null

  statusFilter: EmailKnowledgeStatus | "all" = "pending"

  query = ""

  selectedId: string | null = null

  reloadTrigger = 0

  get selected(): EmailKnowledgeSubmission | null {
    return this.items.find((item) => item.id === this.selectedId) ?? null
  }

  setStatusFilter = (status: EmailKnowledgeStatus | "all") => {
    this.statusFilter = status
  }

  setQuery = (query: string) => {
    this.query = query
  }

  setSelectedId = (id: string | null) => {
    this.selectedId = id
  }

  reload = () => {
    this.reloadTrigger += 1
  }

  load = async () => {
    this.isLoading = true
    this.error = null
    try {
      const response = await emailKnowledgeService.list({
        status: this.statusFilter === "all" ? undefined : this.statusFilter,
        query: this.query || undefined,
        pageNum: 0,
        pageSize: 100,
      })
      this.items = response.data.data.items
      this.total = response.data.data.total

      if (
        this.selectedId &&
        !this.items.some((item) => item.id === this.selectedId)
      ) {
        this.selectedId = this.items[0]?.id ?? null
      } else if (!this.selectedId && this.items.length) {
        this.selectedId = this.items[0].id
      }
    } catch (error) {
      this.error = "Failed to load email knowledge submissions"
      this.items = []
      this.total = 0
    } finally {
      this.isLoading = false
    }
  }

  update = async (id: string, data: EmailKnowledgeUpdatePayload) => {
    this.isActionLoading = true
    this.error = null
    try {
      const response = await emailKnowledgeService.update(id, data)
      this.replaceItem(response.data.data)
      return true
    } catch (error) {
      this.error = "Failed to update submission"
      return false
    } finally {
      this.isActionLoading = false
    }
  }

  approve = async (id: string, data: EmailKnowledgeApprovePayload = {}) => {
    this.isActionLoading = true
    this.error = null
    try {
      const response = await emailKnowledgeService.approve(id, data)
      this.replaceItem(response.data.data)
      return true
    } catch (error) {
      this.error = "Failed to approve submission"
      return false
    } finally {
      this.isActionLoading = false
    }
  }

  reject = async (id: string, reviewNote?: string) => {
    this.isActionLoading = true
    this.error = null
    try {
      const response = await emailKnowledgeService.reject(id, reviewNote)
      this.replaceItem(response.data.data)
      return true
    } catch (error) {
      this.error = "Failed to reject submission"
      return false
    } finally {
      this.isActionLoading = false
    }
  }

  archive = async (id: string) => {
    this.isActionLoading = true
    this.error = null
    try {
      const response = await emailKnowledgeService.archive(id)
      this.replaceItem(response.data.data)
      return true
    } catch (error) {
      this.error = "Failed to archive submission"
      return false
    } finally {
      this.isActionLoading = false
    }
  }

  private replaceItem = (updated: EmailKnowledgeSubmission) => {
    const index = this.items.findIndex((item) => item.id === updated.id)
    if (index >= 0) {
      this.items = [
        ...this.items.slice(0, index),
        updated,
        ...this.items.slice(index + 1),
      ]
    }
  }
}

export default EmailKnowledgeStore
